import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import { useAuth } from '@/composables/useAuth'
import { supportedLocaleCodes, detectLocale } from '@/locales/config'
import { setI18nLocale, loadRouteMessages } from '@/locales'

/** Routes nested under /:locale */
const localeChildren: RouteRecordRaw[] = [
  {
    path: '',
    name: 'home',
    redirect: (to) => ({ name: 'challenges', params: { locale: to.params.locale } }),
  },
  {
    path: 'challenges',
    name: 'challenges',
    component: () => import('@/views/ChallengeListView.vue'),
  },
  {
    path: 'challenges/:id',
    name: 'challenge-detail',
    component: () => import('@/views/ChallengeDetailView.vue'),
    props: (route) => ({ id: route.params.id }),
  },
  {
    path: 'challenges/:id/day/:day',
    name: 'challenge-day',
    component: () => import('@/views/ChallengeDayView.vue'),
    props: (route) => ({ id: route.params.id, day: route.params.day }),
    meta: { requiresAuth: true },
  },
  {
    path: 'profile',
    name: 'profile',
    component: () => import('@/views/ProfileView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: 'create-challenge',
    name: 'create-challenge',
    component: () => import('@/views/CreateChallengeView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: 'login',
    name: 'login',
    component: () => import('@/views/LoginView.vue'),
  },
  {
    path: 'auth/callback',
    name: 'auth-callback',
    component: () => import('@/views/AuthCallbackView.vue'),
  },
  {
    path: 'leaderboard',
    name: 'leaderboard',
    component: () => import('@/views/LeaderboardView.vue'),
  },
  {
    path: 'authors/:slug',
    name: 'author',
    component: () => import('@/views/AuthorPage.vue'),
    meta: { public: true },
  },
  {
    path: 'pricing',
    name: 'pricing',
    component: () => import('@/views/PricingView.vue'),
    meta: { public: true },
  },
  {
    path: 'admin/users',
    name: 'admin-users',
    component: () => import('@/views/AdminUsersView.vue'),
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: 'admin/reports',
    name: 'admin-reports',
    component: () => import('@/views/AdminReportsView.vue'),
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: 'admin/referral',
    name: 'admin-referral',
    component: () => import('@/views/AdminReferralView.vue'),
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: 'referral',
    name: 'referral',
    component: () => import('@/views/ReferralDashboardView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: 'author/apply',
    name: 'author-apply',
    component: () => import('@/views/AuthorApplicationView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: 'payment/success',
    name: 'payment-success',
    component: () => import('@/views/PaymentResultView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: 'payment/fail',
    name: 'payment-fail',
    component: () => import('@/views/PaymentResultView.vue'),
    meta: { public: true },
  },
  {
    path: ':pathMatch(.*)*',
    name: 'locale-not-found',
    component: () => import('@/views/NotFoundView.vue'),
  },
]

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    redirect: () => `/${detectLocale()}/challenges`,
  },
  {
    path: '/:locale',
    children: localeChildren,
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/NotFoundView.vue'),
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, _from, savedPosition) {
    if (savedPosition) return savedPosition
    if (to.hash) return { el: to.hash, behavior: 'smooth' }
    return { top: 0 }
  },
})

router.beforeEach(async (to, _from, next) => {
  const locale = to.params.locale as string

  // Root redirect or top-level catch-all — no locale param
  if (!locale) {
    next()
    return
  }

  // Invalid locale → 404
  if (!supportedLocaleCodes.includes(locale)) {
    next({ name: 'not-found' })
    return
  }

  // Set i18n locale & load route-specific translations
  await setI18nLocale(locale)
  await loadRouteMessages(locale, to.path)

  // Auth / admin guards
  const { isAuthenticated, isAdmin } = useAuth()

  if (to.meta.requiresAuth && !isAuthenticated.value) {
    sessionStorage.setItem('redirectAfterLogin', to.fullPath)
    next({ name: 'login', params: { locale } })
  } else if (to.meta.requiresAdmin && !isAdmin.value) {
    next({ name: 'home', params: { locale } })
  } else {
    next()
  }
})

export default router
